import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IoAdd,
  IoTimeOutline,
  IoTrashOutline,
  IoSearchOutline,
  IoCheckboxOutline,
  IoClose,
  IoCheckmarkDone,
  IoStorefrontOutline,
  IoCubeOutline,
  IoCalendarOutline,
  IoChevronForward,
} from "react-icons/io5";
import { toast } from "sonner";
import api, { fileUrl } from "@/api";
import { useI18n, ALL_STORES, STORE_LABELS } from "@/i18n";
import { formatDateIT } from "@/utils";
import { ConfirmDialog } from "@/components/ConfirmDialog";

export default function ExpiriesListPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [year, setYear] = useState("all");
  const [month, setMonth] = useState("all");
  const [store, setStore] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = store ? { store_id: store } : {};
      const { data } = await api.get("/expiries", { params });
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [store]);
  useEffect(() => { load(); }, [load]);

  const years = useMemo(() => {
    const set = new Set(items.map((i) => new Date(i.expiry_date).getFullYear()));
    return [...set].sort();
  }, [items]);

  const filtered = useMemo(() => {
    return items.filter((it) => {
      const d = new Date(it.expiry_date);
      if (year !== "all" && d.getFullYear() !== Number(year)) return false;
      if (month !== "all" && d.getMonth() + 1 !== Number(month)) return false;
      if (q) {
        const s = (it.product_name + " " + it.store_id + " " + (it.notes || "")).toLowerCase();
        if (!s.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [items, year, month, q]);

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };
  const exitSelectMode = () => { setSelectMode(false); setSelected(new Set()); };
  const selectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((it) => it.id)));
  };
  const bulkDelete = async () => {
    if (selected.size === 0) return;
    await api.post("/expiries/bulk_delete", { ids: [...selected] });
    toast.success("Eliminati");
    exitSelectMode(); load();
  };

  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const withBar = selectMode && selected.size > 0;

  return (
    <div className={`mobile-frame ${withBar ? "with-bottom-bar" : ""}`} data-testid="expiries-page">
      {selectMode ? (
        <div className="tf-header-simple">
          <button data-testid="select-cancel" className="tf-close-btn" onClick={exitSelectMode}>
            <IoClose size={22} />
          </button>
          <div className="tf-header-title-center">
            {selected.size === 0
              ? t("select_all")
              : selected.size === 1
              ? t("selected_count_1")
              : t("selected_count_n").replace("$", selected.size)}
          </div>
          <button data-testid="select-all-btn" className="tf-icon-btn ghost" onClick={selectAll}>
            <IoCheckmarkDone size={20} color="#E2001A" />
          </button>
        </div>
      ) : (
        <div className="tf-header-simple">
          <button data-testid="back-button" className="tf-close-btn" onClick={() => navigate("/")}>
            <IoChevronForward size={22} style={{ transform: "rotate(180deg)" }} />
          </button>
          <div className="tf-header-title-center" data-testid="page-title">{t("expiries")}</div>
          <button data-testid="header-select-btn" className="tf-icon-btn ghost" onClick={() => { setSelectMode(true); setSelected(new Set()); }} aria-label={t("select_all")}>
            <IoCheckboxOutline size={20} />
          </button>
        </div>
      )}

      <div className="tf-expired-header">
        <div className="tf-expired-count-card" data-testid="expired-count">
          <div className="tf-expired-count-number">{filtered.length}</div>
          <div className="tf-expired-count-label">{t("total_expired")}</div>
        </div>

        <div className="tf-input-wrap" style={{ marginBottom: 14 }}>
          <IoSearchOutline size={20} className="tf-input-icon-left" style={{ color: "#9CA3AF" }} />
          <input
            data-testid="expiries-search"
            className="tf-input tf-input-with-icon-left"
            placeholder={t("search")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        <div className="tf-chips-row">
          <button data-testid="year-all" className={`tf-chip ${year === "all" ? "active-yellow" : ""}`} onClick={() => setYear("all")}>
            {t("all_years")}
          </button>
          {years.map((y) => (
            <button key={y} data-testid={`year-${y}`} className={`tf-chip ${String(year) === String(y) ? "active-yellow" : ""}`} onClick={() => setYear(y)}>
              {y}
            </button>
          ))}
        </div>
        <div className="tf-chips-row" style={{ marginTop: -4 }}>
          <button data-testid="month-all" className={`tf-chip ${month === "all" ? "active-yellow" : ""}`} onClick={() => setMonth("all")}>
            {t("all_months")}
          </button>
          {months.map((m) => (
            <button key={m} data-testid={`month-${m}`} className={`tf-chip ${String(month) === String(m) ? "active-yellow" : ""}`} onClick={() => setMonth(m)}>
              {t(`month_${m}`)}
            </button>
          ))}
        </div>

        <div className="tf-chips-row" style={{ marginTop: -4 }}>
          <button className={`tf-chip ${!store ? "active-yellow" : ""}`} onClick={() => setStore("")}>
            {t("all_stores")}
          </button>
          {ALL_STORES.map((s) => (
            <button key={s} className={`tf-chip ${store === s ? "active-yellow" : ""}`} onClick={() => setStore(s)}>
              {t(STORE_LABELS[s])}
            </button>
          ))}
        </div>
      </div>

      <div style={{ backgroundColor: "var(--tf-surface)", borderRadius: "20px 20px 0 0", minHeight: 200, marginTop: -8, position: "relative" }}>
        {loading ? (
          <div className="tf-empty">{t("loading")}</div>
        ) : filtered.length === 0 ? (
          <div className="tf-empty">
            <IoTimeOutline size={44} />
            <div>{t("no_expiries")}</div>
          </div>
        ) : (
          <div>
            {filtered.map((it) => {
              const isSelected = selected.has(it.id);
              return (
                <div
                  key={it.id}
                  data-testid={`expiry-item-${it.id}`}
                  className="tf-expired-list-item"
                  onClick={() => selectMode ? toggle(it.id) : navigate(`/expiry/${it.id}`)}
                  style={{
                    backgroundColor: isSelected ? "var(--tf-red-soft)" : "var(--tf-surface)",
                    position: "relative",
                  }}
                >
                  {selectMode && (
                    <div style={{
                      position: "absolute", top: 12, right: 12,
                      width: 26, height: 26, borderRadius: 999,
                      border: `2px solid ${isSelected ? "var(--tf-red)" : "var(--tf-border)"}`,
                      backgroundColor: isSelected ? "var(--tf-red)" : "var(--tf-surface)",
                      color: "white",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {isSelected && <IoCheckmarkDone size={14} />}
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span className="tf-priority tf-priority-high">{t("expired_status")}</span>
                    <div style={{ fontFamily: "Manrope, sans-serif", fontWeight: 900, fontSize: 18, marginTop: 4, letterSpacing: "-0.01em", color: "var(--tf-text)" }}>
                      {it.product_name}
                    </div>
                    <div className="tf-small tf-row" style={{ marginTop: 4, gap: 8, flexWrap: "wrap" }}>
                      <span className="tf-row" style={{ gap: 4 }}><IoStorefrontOutline size={13} />{t(STORE_LABELS[it.store_id])}</span>
                      <span className="tf-row" style={{ gap: 4 }}><IoCubeOutline size={13} />{it.quantity} pz</span>
                    </div>
                    <div className="tf-small tf-row" style={{ marginTop: 4, gap: 6 }}>
                      <IoCalendarOutline size={13} />
                      <span style={{ fontWeight: 700, color: "var(--tf-text)" }}>{t("expiry_short")} {formatDateIT(it.expiry_date)}</span>
                    </div>
                  </div>
                  {!selectMode && <IoChevronForward size={20} color="var(--tf-muted)" style={{ flexShrink: 0 }} />}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {!selectMode && (
        <button data-testid="fab-add-expiry" className="tf-fab black" onClick={() => navigate("/expiry/new")}>
          <IoAdd size={22} />
          {t("add_product")}
        </button>
      )}

      {withBar && (
        <div className="tf-select-bottom-bar" data-testid="select-bottom-bar" style={{ gridTemplateColumns: "1fr" }}>
          <button data-testid="bulk-delete-btn" onClick={() => setConfirmOpen(true)}>
            <IoTrashOutline size={22} />
            {t("delete")}
          </button>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={bulkDelete}
        title={t("confirm_delete")}
        description={selected.size === 1 ? t("selected_count_1") : t("selected_count_n").replace("$", selected.size)}
        confirmLabel={t("delete")}
      />
    </div>
  );
}
